import { useDroppable } from "@dnd-kit/core";
import type React from "react";
import { useRef, useState } from "react";
import { useDndState } from "../context/DndProvider";
import type { DropData } from "../types";

interface DroppableZoneProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  data: DropData;
  id: string;
  allowedType: string[];
  className?: string;
  defaultBorderClass?: string;
  onLocalFileDrop?: (file: File) => void;
  localFileAccept?: string;
  clickable?: boolean;
  /** Merged over `style` while a compatible item is being dragged anywhere on the page. */
  compatibleStyle?: React.CSSProperties;
  /** Merged over `style` while a compatible item is held over this zone. Needed by zones whose
   *  border and background are inline (computed sizes), where the class-based cues never show. */
  overStyle?: React.CSSProperties;
}

export function DroppableZone({
  children,
  data,
  id,
  allowedType,
  className,
  defaultBorderClass = "border-violet-200",
  onLocalFileDrop,
  localFileAccept,
  clickable,
  compatibleStyle,
  overStyle,
  style,
  ...props
}: DroppableZoneProps) {
  const { isOver, setNodeRef } = useDroppable({ id, data });
  const { draggedItem } = useDndState();
  const [isLocalFileDragOver, setIsLocalFileDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (onLocalFileDrop && e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      e.stopPropagation();
      setIsLocalFileDragOver(true);
    }
    props.onDragOver?.(e);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    setIsLocalFileDragOver(false);
    props.onDragLeave?.(e);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (onLocalFileDrop && e.dataTransfer.files.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setIsLocalFileDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) onLocalFileDrop(file);
    }
    props.onDrop?.(e);
  };

  const isCompatible = draggedItem && allowedType.includes(draggedItem.type);
  const isTargeted = (isCompatible && isOver) || isLocalFileDragOver;

  const mergedStyle = {
    ...style,
    ...(isCompatible ? compatibleStyle : {}),
    ...(isTargeted ? overStyle : {}),
  };

  const borderClass = isLocalFileDragOver
    ? "border-4 border-blue-500 border-dashed bg-blue-50"
    : isCompatible
      ? isOver
        ? "border-4 border-blue-500 border-dashed bg-blue-50"
        : "border-2 border-blue-300 border-dashed bg-blue-50/30"
      : defaultBorderClass;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!clickable) return;

    if (onLocalFileDrop && fileInputRef.current) {
      fileInputRef.current.click();
    }
    props.onClick?.(e);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onLocalFileDrop?.(file);
    e.target.value = "";
  };

  return (
    <div
      ref={setNodeRef}
      className={`${borderClass} ${className || ""}`}
      style={mergedStyle}
      {...props}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      {onLocalFileDrop && (
        <input
          ref={fileInputRef}
          type="file"
          accept={localFileAccept}
          className="hidden"
          onChange={handleFileInputChange}
        />
      )}
      {children}
    </div>
  );
}
